<template>
    <slide-over-panel
        v-model:show="$data._show"
        @update:show="updateShow($data._show)"
    >
        <template #title>
            <span v-if="edit_profile" class="text-xl">Edit Profile</span>
            <span v-if="new_profile" class="text-xl">New Profile</span>
        </template>

        <template #content>

            <banner-error v-if="errors && errors.length > 0" class="mb-4">
                <template #title>
                    <span class="text-xl">Please fix the following errors</span>
                </template>
                <template #content>
                    <ul class="mb-0 list-disc pl-5">
                        <li v-for="(error, index) in errors" :key="index">
                            <p class="text-red-800">{{ error }}</p>
                        </li>
                    </ul>
                </template>
            </banner-error>

            <banner v-if="this.profile.id === 1">
                <template #title>
                    <span class="text-xl">Default Profile</span>
                </template>
                <template #content>
                    <p class="text-gray-500">This is the default profile and cannot be modified.</p>
                </template>
            </banner>

            <div class="mt-4 flex flex-col items-start sm:flex-row sm:gap-4">
                <div class="flex w-full flex-col sm:w-2/3">
                    <div class="flex flex-row items-center justify-between">
                        <label for="profile_name" aria-hidden="true">Name</label>
                    </div>
                    <input type="text" v-model="this.profile.name" id="profile_name" name="profile_name" :class="{'border-red-800' : errors?.name}" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm" aria-label="Profile Name Input">
                </div>

                <div class="flex w-full flex-col sm:w-1/3">
                    <div class="flex flex-row items-center justify-between">
                        <label for="profile_units" aria-hidden="true">Units <span class="text-xs text-gray-500">(Optional)</span></label>
                    </div>
                    <input type="text" v-model="this.profile.units" id="profile_units" name="profile_units" :class="{'border-red-800' : errors?.units}" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm" aria-label="Units Input">
                    <p class="text-xs text-gray-500 mt-1" aria-hidden="true">Label that shows after the value denoting the unit type</p>
                </div>
            </div>

            <div class="mt-4 flex flex-col items-start sm:flex-row sm:gap-4">
                <div class="flex w-full flex-col">
                    <div class="flex w-full flex-row items-center justify-between">
                        <label for="profile_slope" aria-hidden="true">Slope</label>
                    </div>
                    <input type="number" v-model="this.profile.slope" id="profile_slope" name="profile_slope" :class="{'border-red-800' : errors?.slope}" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm" aria-label="Slope Input">
                    <p class="text-xs text-gray-500 mt-1" aria-hidden="true">Slope (m) in the calculation y = mx + b</p>
                </div>

                <div class="flex w-full flex-col">
                    <div class="flex w-full flex-row items-center justify-between">
                        <label for="profile_intercept" aria-hidden="true">Intercept</label>
                    </div>
                    <input type="number" v-model="this.profile.intercept" id="profile_intercept" name="profile_intercept" :class="{'border-red-800' : errors?.intercept}" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm" aria-label="Intercept Input">
                    <p class="text-xs text-gray-500 mt-1" aria-hidden="true">Intercept (b) in the calculation y = mx + b</p>
                </div>
            </div>

            <div class="mt-4 flex flex-col items-start sm:flex-row sm:gap-4">
                <div class="flex w-full flex-col sm:w-1/3">
                    <div class="flex flex-row items-center justify-between">
                        <label for="range_min" aria-hidden="true">Range Min <span class="text-xs text-gray-500">(Optional)</span></label>
                    </div>
                    <input type="number" v-model="this.profile.range_min" id="range_min" name="range_min" :class="{'border-red-800' : errors?.range_min}" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm" aria-label="Range Min Input">
                    <p class="text-xs text-gray-500 mt-1" aria-hidden="true">Minimum value that can occur</p>
                </div>

                <div class="flex w-full flex-col sm:w-1/3">
                    <div class="flex flex-row items-center justify-between">
                        <label for="range_max" aria-hidden="true">Range Max <span class="text-xs text-gray-500">(Optional)</span></label>
                    </div>
                    <input type="number" v-model="this.profile.range_max" id="range_max" name="range_max" :class="{'border-red-800': errors?.range_max}" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm" aria-label="Range Max Input">
                    <p class="text-xs text-gray-500 mt-1" aria-hidden="true">Maximum value that can occur</p>
                </div>

                <div class="flex w-full flex-col sm:w-1/3">
                    <div class="flex flex-row items-center justify-between">
                        <label for="profile_precision" aria-hidden="true">Precision</label>
                    </div>
                    <input type="number" min="0" max="100" v-model="this.profile.precision" id="profile_precision" name="profile_precision" :class="{'border-red-800' : errors?.precision}" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm" aria-label="Precision Input">
                    <p class="text-xs text-gray-500 mt-1" aria-hidden="true">Number of digits after the decimal point</p>
                </div>
            </div>

            <div class="mt-4 flex flex-col">
                <button class="flex w-full items-center justify-between" @click="toggleAdvancedSettings">
                    <span v-if="! advanced_settings">Show Advanced Settings</span>
                    <span v-if="advanced_settings">Hide Advanced Settings</span>
                    <span v-if="! advanced_settings" class="lnr icon-chevron-down !text-base"></span>
                    <span v-if="advanced_settings" class="lnr icon-chevron-up !text-base"></span>
                </button>

                <hr class="my-2 h-px border-0 bg-gray-200">

                <div v-if="advanced_settings">
                    <div v-if="! edit_profile" class="mt-3 flex flex-col">
                        <div class="flex flex-row items-center justify-between">
                            <label for="profile_type" aria-hidden="true">Type</label>
                        </div>

                        <select2 :options="profile_types"
                                 class="rounded-none border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
                                 :close-on-select="true"
                                 v-model:value="this.profile.type"
                                 id="profile_type"
                                 name="profile_type">
                        </select2>
                    </div>

                    <div class="mt-3 flex flex-col">
                        <div class="flex">
                            <label for="suppress">Suppress On Floorplan</label>
                        </div>
                        <toggle
                            v-model:value="this.profile.suppress"
                            id="suppress"
                            name="suppress">
                        </toggle>
                        <p class="text-xs text-gray-500" aria-hidden="true">Hide value on the floorplan view</p>
                    </div>
                </div>
            </div>

            <div v-if="this.edit_profile" class="mt-6 flex justify-end gap-4 pb-4">
                <button-red-secondary class="w-full max-w-none" @click="destroyProfile">
                    Delete Profile
                </button-red-secondary>

                <button-blue-primary class="w-full max-w-none" @click="updateProfile">
                    Update Profile
                </button-blue-primary>
            </div>

            <div v-if="this.new_profile" class="mt-6 flex justify-end pb-4">
                <button-blue-primary class="w-1/2 max-w-none" @click="storeProfile">
                    Add Profile
                </button-blue-primary>
            </div>
        </template>
    </slide-over-panel>

    <ajax-table :data="data"
                :headers="headers"
                v-model:loading="ajaxTable.loading"
                v-model:sort-by="ajaxTable.sortBy"
                v-model:sort-desc="ajaxTable.sortDesc"
                v-model:page="ajaxTable.page"
                v-model:page-info="ajaxTable.pageInfo"
                :exportable="false"
                :search-enabled="false"
                :items-per-page-enabled="false"
                @loadData="getData"
                @rowSelected="editProfile"
                no-data-message="No profiles found"
    >
        <template #above_table_center>
            <button-green-primary class="float-right max-w-80" @click="createProfile">
                Add New Profile
                <span class="ml-2 lnr icon-plus-circle !text-base"></span>
            </button-green-primary>
        </template>

        <template #col-1="props">
            <div class="flex items-center">
                <div class="flex flex-col">
                    <div class="flex flex-row text-base text-gray-900">
                        {{ props.row.name }}

                        <div class="flex flex-wrap items-center justify-between gap-2">
                            <span v-if="props.row.units !== ''" class="ml-1 rounded-full bg-green-100 px-2 text-sm text-green-800 py-0.5">
                                {{ props.row.units }}
                            </span>
                            <span v-if="props.row.suppress" class="ml-1 rounded-full bg-red-100 px-2 text-sm text-red-800 py-0.5">
                                Suppressed
                            </span>
                        </div>
                    </div>
                    <span v-if="(props.row.type !== 'default')" class="text-xs text-gray-500">{{ props.row.type }}</span>
                </div>
            </div>
        </template>

        <template #col-2="props">
            <div class="flex items-center">
                <div class="grid grid-row-2">
                    <div class="grid grid-cols-2 gap-4">
                        <span class="text-xs text-gray-500">Slope</span>
                        <span>{{ props.row.slope }}</span>
                    </div>
                    <div class="grid grid-cols-2 gap-4">
                        <span class="text-xs text-gray-500">Intercept</span>
                        <span>{{ props.row.intercept }}</span>
                    </div>
                </div>
            </div>
        </template>

        <template #col-3="props">
            <div class="flex items-center">
                <div class="flex flex-col">
                    <span>
                        {{ exampleValue(props.row.precision) }}
                        <span v-if="props.row.units">{{ props.row.units}}</span> </span>
                    <span class="text-xs text-gray-500">Precision {{ props.row.precision }}</span>
                    <span v-if="props.row.range_min !== null && props.row.range_max !== null" class="text-xs text-gray-500">Range {{ props.row.range_min }} - {{ props.row.range_max }}</span>
                </div>
            </div>
        </template>
    </ajax-table>
</template>


<script>
import ButtonBluePrimary from "@/components/elements/buttons/ButtonBluePrimary.vue";
import ButtonGreenPrimary from "@/components/elements/buttons/ButtonGreenPrimary.vue";
import ButtonRedPrimary from "@/components/elements/buttons/ButtonRedPrimary.vue";
import ButtonRedSecondary from "@/components/elements/buttons/ButtonRedSecondary.vue";

import Banner from "@/components/Banners/Banner.vue";
import BannerError from "@/components/Banners/BannerError.vue";

import AjaxTable from "@/components/elements/AjaxTable.vue";
import Select2 from "@/components/elements/select2.vue";
import SlideOverPanel from "@/components/elements/SlideOverPanel.vue";
import Toggle from "@/components/elements/toggle.vue";

import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {ajaxTableMixin} from "@/mixins/ajaxTableMixin";

import axios from "axios";
import _ from "lodash";

export default {
    name: 'CurrentProfilesIndex',
    components: {
        ButtonBluePrimary,
        ButtonGreenPrimary,
        ButtonRedPrimary,
        ButtonRedSecondary,
        Banner,
        BannerError,
        AjaxTable,
        Select2,
        SlideOverPanel,
        Toggle
    },
    mixins: [ajaxTableMixin, SonicuCoreMixin],
    props:{
        profile_types: {
            type: Object,
            default: () => {}
        },
    },
    data() {
        return {
            data: [],
            headers: [
                {
                    name: 'Name',
                    key: 'ProfileId'
                },
                {
                    name: 'Slope Intercept',
                    value: 'Slope'
                },
                {
                    name: 'Value',
                    value: 'type'
                }
            ],
            _show: this.show,
            edit_profile: false,
            new_profile: false,
            profile: {},
            errors: null,
            advanced_settings: false
        };
    },
    methods: {
        async getData() {
            try {
                this.ajaxTable.loading = true;
                const response = await axios.get(this.company.buildCompanyApiInternalRoute('point-profiles'));
                this.data = response.data.data;
                this.ajaxUpdateTableData(this.data);
            }
            catch (e) {
                console.error(e);
            }
            finally {
                this.ajaxTable.loading = false;
            }
        },
        exampleValue(precision) {
            return Math.PI.toFixed(precision);
        },
        updateShow(show) {
            this.$data._show = show
            this.$emit('update:show', this.$data._show)
            this.advanced_settings = false;
            if (!show) {
                this.errors = null;
            }
        },
        toggleAdvancedSettings() {
            this.advanced_settings = !this.advanced_settings;
        },
        editProfile(index) {
            this.edit_profile = true;
            this.new_profile = false;
            this.errors = null;
            this.updateShow(true);
            this.profile = _.clone(this.data[index]); // Clone the profile to avoid mutating the original data
        },
        createProfile() {
            this.new_profile = true;
            this.edit_profile = false;
            this.profile = {
                type: 'default',
            };
            this.errors = null;
            this.updateShow(true);
        },
        storeProfile() {
            axios.post(this.company.buildCompanyRoute('point-profiles'), this.profile)
                .then(() => {
                    this.getData();
                    this.updateShow(false);
                })
                .catch(error => {
                    const rawErrors = error.response.data.errors;
                    this.errors = Object.values(rawErrors).flat();
                    console.error(error);
                });
        },
        updateProfile() {
            axios.post(this.company.buildCompanyRoute('point-profiles') + '/' + this.profile.id, this.profile)
                .then(() => {
                    this.getData();
                    this.updateShow(false);
                })
                .catch(error => {
                    const rawErrors = error.response.data.errors;
                    this.errors = Object.values(rawErrors).flat();
                    console.error(this.errors);
                });
        },
        destroyProfile() {
            this.profile._method = 'DELETE';
            axios.post(this.company.buildCompanyRoute('point-profiles') + '/' + this.profile.id, this.profile)
                .catch(error => {
                    const rawErrors = error.response.data.errors;
                    this.errors = Object.values(rawErrors).flat();
                })
                .finally(() => {
                    this.getData();
                    this.updateShow(false);
                })
            ;
        }
    },
};
</script>